import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useProviderTypes, useCreateProvider, useUpdateProvider } from '@/hooks/useProviders';
import { useProviderStore } from '@/stores/providerStore';
import { providerSchema, CreateProviderInput, CURRENCIES } from '../types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorDisplay } from '@/components/common/ErrorBoundary';
import { ImageUpload } from './ImageUpload';
import { ProviderType } from '@/features/providers/types';
import { showToast } from '@/utils/toast';

export function ProviderForm() {

    const { selectedProvider, closeCreateModal } = useProviderStore();
    const { data: providerTypes = [], isLoading: typesLoading, error: typesError } = useProviderTypes();
    const createMutation = useCreateProvider();
    const updateMutation = useUpdateProvider();

    const form = useForm<CreateProviderInput>({
        resolver: zodResolver(providerSchema),
        defaultValues: {
            name: '',
            type_id: undefined,
            logo_url: null,
            logo_filename: null,
            website_url: null,
            support_email: null,
            support_phone: null,
            free_trial_days: null,
            has_sequence_numbers: false,
            currency: 'USD'
        }
    });

    // Set form values when editing
    useEffect(() => {
        if (selectedProvider) {
            form.reset({
                name: selectedProvider.name,
                type_id: selectedProvider.type_id,
                logo_url: selectedProvider.logo_url || null,
                logo_filename: selectedProvider.logo_filename || null,
                website_url: selectedProvider.website_url || null,
                support_email: selectedProvider.support_email || null,
                support_phone: selectedProvider.support_phone || null,
                free_trial_days: selectedProvider.free_trial_days || null,
                has_sequence_numbers: selectedProvider.has_sequence_numbers || false,
                currency: selectedProvider.currency || 'USD'
            });
        }
    }, [selectedProvider, form]);

    const onSubmit = async (data: CreateProviderInput) => {
        try {
            console.log('Submitting provider form with data:', data);

            if (selectedProvider) {
                // When updating, never include has_sequence_numbers as it cannot be changed after channels exist
                const { has_sequence_numbers, ...updateData } = data;

                await updateMutation.mutateAsync({ 
                    id: selectedProvider.id, 
                    data: updateData 
                });
                showToast.updated('Provider');
            } else {
                await createMutation.mutateAsync(data);
                showToast.success('Provider created successfully');
            }
            closeCreateModal();
        } catch (error) {
            console.error('Form submission error:', error);
            showToast.error(error instanceof Error ? error.message : 'Failed to save provider');
        }
    };

    if (typesLoading) {
        return <LoadingSpinner />;
    }

    if (typesError instanceof Error) {
        return <ErrorDisplay error={typesError} />;
    }

    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Provider Name</FormLabel>
                            <FormControl>
                                <Input {...field} placeholder="Enter provider name" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="type_id"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Provider Type</FormLabel>
                            <Select
                                onValueChange={(value) => field.onChange(parseInt(value))}
                                value={field.value?.toString()}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select provider type" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {Array.isArray(providerTypes) && providerTypes.map((type: ProviderType) => (
                                        <SelectItem key={type.id} value={type.id.toString()}>
                                            {type.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Currency</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                value={field.value || ''}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select currency" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {CURRENCIES.map((currency) => (
                                        <SelectItem key={currency.code} value={currency.code}>
                                            {currency.symbol} {currency.name} ({currency.code})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormDescription>
                                The currency used for pricing in this provider's region
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="logo_url"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Logo</FormLabel>
                            <FormControl>
                                <ImageUpload
                                    value={field.value || ''}
                                    onChange={(url: string, filename: string) => {
                                        field.onChange(url);
                                        form.setValue('logo_filename', filename);
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="has_sequence_numbers"
                    render={({ field }) => (
                        <FormItem>
                            <div className="flex items-center gap-2">
                                <FormControl>
                                    <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        disabled={selectedProvider !== null}
                                    />
                                </FormControl>
                                <div className="space-y-0.5">
                                    <FormLabel>Use Sequential Channel Numbers</FormLabel>
                                    <FormDescription>
                                        Automatically assign sequential numbers to channels. This cannot be changed after creation.
                                    </FormDescription>
                                </div>
                            </div>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="website_url"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Website URL</FormLabel>
                            <FormControl>
                                <Input 
                                    {...field} 
                                    value={field.value || ''} 
                                    placeholder="https://www.example.com" 
                                    type="url"
                                />
                            </FormControl>
                            <FormDescription>
                                Official website URL for the provider
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="support_email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Support Email</FormLabel>
                            <FormControl>
                                <Input 
                                    {...field} 
                                    value={field.value || ''} 
                                    placeholder="support@example.com" 
                                    type="email"
                                />
                            </FormControl>
                            <FormDescription>
                                Customer support email address
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="support_phone"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Support Phone</FormLabel>
                            <FormControl>
                                <Input 
                                    {...field} 
                                    value={field.value || ''} 
                                    placeholder="+1-800-123-4567" 
                                    type="tel"
                                />
                            </FormControl>
                            <FormDescription>
                                Customer support phone number
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="free_trial_days"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Free Trial Days</FormLabel>
                            <FormControl>
                                <Input 
                                    {...field} 
                                    value={field.value?.toString() || ''} 
                                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                                    placeholder="7" 
                                    type="number"
                                    min="0"
                                    max="365"
                                />
                            </FormControl>
                            <FormDescription>
                                Number of free trial days offered to new customers
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={closeCreateModal}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <LoadingSpinner className="mr-2 h-4 w-4" />}
                        {selectedProvider ? 'Update' : 'Create'} Provider
                    </Button>
                </div>
            </form>
        </Form>
    );
}